
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import certificateService from "../services/certificateService";

const MAX_FILE_SIZE = 512 * 1024;
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".zip"];

const INITIAL_FORM = {
  certificate_type_id: "",
  certificate_name: "",
  certificate_number: "",
  issuing_authority: "",
  issued_to: "",
  branch: "",
  issue_date: "",
  expiry_date: "",
  asset_reference_id: "",
  remarks: "",
};

const styles = {
  page: {
    minHeight: "100%",
    padding: "24px",
    background: "#F7F5FC",
    color: "#29243A",
    fontFamily: "inherit",
  },
  header: {
    background:
      "linear-gradient(120deg, #4C1D95 0%, #7C3AED 55%, #A855F7 100%)",
    borderRadius: 16,
    padding: "26px 28px",
    color: "#FFFFFF",
    marginBottom: 22,
    boxShadow: "0 8px 24px rgba(91, 33, 182, 0.16)",
  },
  card: {
    background: "#FFFFFF",
    border: "1px solid #E9E3F5",
    borderRadius: 14,
    padding: 24,
    marginBottom: 20,
    boxShadow: "0 3px 12px rgba(40, 20, 70, 0.035)",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    margin: "0 0 22px",
    paddingBottom: 13,
    borderBottom: "1px solid #EEE8F7",
    color: "#4C1D95",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "20px 22px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
    minWidth: 0,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "#514765",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: 42,
    padding: "10px 12px",
    border: "1px solid #DDD5EA",
    borderRadius: 8,
    background: "#FFFFFF",
    color: "#29243A",
    fontSize: 13,
    outlineColor: "#8B5CF6",
  },
  readOnly: {
    background: "#F5F1FB",
    color: "#7C6C91",
    cursor: "not-allowed",
  },
  error: {
    color: "#B42318",
    fontSize: 12,
    marginTop: 2,
  },
  upload: {
    border: "2px dashed #B9A0E8",
    borderRadius: 12,
    padding: "28px 20px",
    textAlign: "center",
    background: "linear-gradient(180deg, #FCFAFF, #F7F1FF)",
  },
  primaryButton: {
    border: 0,
    borderRadius: 9,
    padding: "11px 21px",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    background: "linear-gradient(110deg, #6D28D9, #A855F7)",
    boxShadow: "0 4px 12px rgba(124, 58, 237, 0.2)",
  },
  secondaryButton: {
    border: "1px solid #D9CDEA",
    borderRadius: 9,
    padding: "11px 21px",
    color: "#5B21B6",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    background: "#FFFFFF",
  },
};

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  readOnly = false,
  error = "",
}) {
  return (
    <div style={styles.field}>
      <label htmlFor={name} style={styles.label}>
        {label}
        {required ? " *" : ""}
      </label>

      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        readOnly={readOnly}
        placeholder={placeholder}
        style={{
          ...styles.input,
          ...(readOnly ? styles.readOnly : {}),
          ...(error ? { borderColor: "#DC2626" } : {}),
        }}
      />

      {error && <span style={styles.error}>{error}</span>}
    </div>
  );
}

export default function CertificateCreatePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(INITIAL_FORM);
  const [certificateTypes, setCertificateTypes] = useState([]);
  const [typeSearch, setTypeSearch] = useState("");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadTypes() {
      try {
        const rows = await certificateService.listCertificateTypes();

        if (mounted) {
          setCertificateTypes(Array.isArray(rows) ? rows : []);
        }
      } catch (error) {
        if (mounted) {
          setPageError(
            error?.response?.data?.message ||
              "Unable to load certificate types."
          );
        }
      } finally {
        if (mounted) setLoadingTypes(false);
      }
    }

    loadTypes();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredTypes = certificateTypes.filter((item) => {
    const search = typeSearch.trim().toLowerCase();

    return (
      !search ||
      String(item.type_name || "").toLowerCase().includes(search) ||
      String(item.type_code || "").toLowerCase().includes(search)
    );
  });

  function handleChange(event) {
    const { name, value } = event.target;

    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setPageError("");
  }

  function chooseType(item) {
    setForm((previous) => ({
      ...previous,
      certificate_type_id: String(item.id),
    }));
    setTypeSearch(item.type_name || "");
    setTypeDropdownOpen(false);
    setErrors((previous) => ({ ...previous, certificate_type_id: "" }));
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];

    if (!file) return;

    const extensionIndex = file.name.lastIndexOf(".");
    const extension =
      extensionIndex >= 0
        ? file.name.substring(extensionIndex).toLowerCase()
        : "";

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setErrors((previous) => ({
        ...previous,
        file: "Only PDF, JPG, PNG, and ZIP files are allowed.",
      }));
      setSelectedFile(null);
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrors((previous) => ({
        ...previous,
        file: "The file exceeds the maximum allowed size of 512 KB.",
      }));
      setSelectedFile(null);
      event.target.value = "";
      return;
    }

    setSelectedFile(file);
    setErrors((previous) => ({ ...previous, file: "" }));
  }

  function removeFile() {
    setSelectedFile(null);
    setErrors((previous) => ({ ...previous, file: "" }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function validateForm() {
    const nextErrors = {};

    if (!form.certificate_type_id) {
      nextErrors.certificate_type_id = "Select a certificate type.";
    }

    if (!form.certificate_name.trim()) {
      nextErrors.certificate_name = "Certificate name is required.";
    }

    if (
      form.issue_date &&
      form.expiry_date &&
      form.expiry_date < form.issue_date
    ) {
      nextErrors.expiry_date =
        "Expiry date cannot be earlier than the issue date.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  
  async function handleSubmit(event) {
    event.preventDefault();
    setPageError("");

    if (!validateForm()) return;

    setSaving(true);

    let certificateCreated = false;

    try {
      const payload = {
        ...form,
        certificate_type_id: Number(form.certificate_type_id),
        certificate_name: form.certificate_name.trim(),
        certificate_number: form.certificate_number.trim() || null,
        issuing_authority: form.issuing_authority.trim() || null,
        issued_to: form.issued_to.trim() || null,
        branch: form.branch.trim() || null,
        issue_date: form.issue_date || null,
        expiry_date: form.expiry_date || null,
        asset_reference_id: form.asset_reference_id.trim() || null,
        remarks: form.remarks.trim() || null,
      };

      // Step 1: Create the certificate record.
      const certificate = await certificateService.createCertificate(payload);
      certificateCreated = true;

      // Step 2: Upload the selected document, if any.
      if (selectedFile) {
        const certificateId = certificate?.id;

        if (!certificateId) {
          setPageError(
            "The certificate was created, but its ID was not returned. " +
              "The document was not uploaded. Please check the certificate register before retrying."
          );
          return;
        }

        try {
          await certificateService.uploadCertificateDocument(
            certificateId,
            selectedFile,
            form.remarks.trim()
          );
        } catch (uploadError) {
          setPageError(
            "The certificate was created successfully, but the document upload failed. " +
              (uploadError?.response?.data?.message ||
                "Please check the certificate register before retrying.")
          );
          return;
        }
      }

      navigate("/assets/certificate-management", {
        replace: true,
        state: {
          message: selectedFile
            ? "Certificate and document uploaded successfully."
            : "Certificate created successfully.",
        },
      });
    } catch (error) {
      setPageError(
        certificateCreated
          ? "The certificate was created, but a later operation failed. Please check the certificate register before retrying."
          : error?.response?.data?.message ||
              "Unable to create the certificate. Please try again."
      );
    } finally {
      setSaving(false);
    }
  }

  
  async function handleSubmit_old(event) {
    event.preventDefault();
    setPageError("");

    if (!validateForm()) return;

    if (selectedFile) {
      setPageError(
        "The upload interface and file validation are ready, but file storage has not been connected yet. The certificate has not been saved. We must connect the backend upload endpoint before saving a certificate with an attachment."
      );
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        certificate_type_id: Number(form.certificate_type_id),
        certificate_name: form.certificate_name.trim(),
        certificate_number: form.certificate_number.trim() || null,
        issuing_authority: form.issuing_authority.trim() || null,
        issued_to: form.issued_to.trim() || null,
        branch: form.branch.trim() || null,
        issue_date: form.issue_date || null,
        expiry_date: form.expiry_date || null,
        asset_reference_id: form.asset_reference_id.trim() || null,
        remarks: form.remarks.trim() || null,
      };

      await certificateService.createCertificate(payload);

      navigate("/assets/certificate-management", {
        replace: true,
        state: { message: "Certificate created successfully." },
      });
    } catch (error) {
      setPageError(
        error?.response?.data?.message ||
          "Unable to create the certificate. Please try again."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 15,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 13,
              display: "grid",
              placeItems: "center",
              background: "rgba(255,255,255,0.17)",
              fontSize: 27,
            }}
          >
            ✦
          </div>

          <div>
            <div style={{ fontSize: 24, fontWeight: 750 }}>
              Add Certificate
            </div>
            <div style={{ fontSize: 13, marginTop: 6, opacity: 0.9 }}>
              Certificate Management / Certificate Register / New
            </div>
          </div>
        </div>

        <p style={{ fontSize: 13, lineHeight: 1.6, margin: "18px 0 0" }}>
          Register certificate information, validity dates, and the
          supporting certificate document.
        </p>
      </div>

      {pageError && (
        <div
          role="alert"
          style={{
            padding: 13,
            marginBottom: 18,
            borderRadius: 9,
            border: "1px solid #FECACA",
            background: "#FEF2F2",
            color: "#991B1B",
            fontSize: 13,
          }}
        >
          {pageError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>01. Certificate Information</h2>

          <div style={styles.grid}>
            <Field
              label="Certificate Code"
              name="certificate_code"
              value="Generated by the system"
              readOnly
            />

            <div style={styles.field}>
              <label htmlFor="certificate_type_search" style={styles.label}>
                Certificate Type *
              </label>

              <div style={{ position: "relative" }}>
                <input
                  id="certificate_type_search"
                  value={typeSearch}
                  onChange={(event) => {
                    setTypeSearch(event.target.value);
                    setForm((previous) => ({
                      ...previous,
                      certificate_type_id: "",
                    }));
                    setTypeDropdownOpen(true);
                  }}
                  onFocus={() => setTypeDropdownOpen(true)}
                  onBlur={() =>
                    setTimeout(() => setTypeDropdownOpen(false), 150)
                  }
                  placeholder={
                    loadingTypes
                      ? "Loading certificate types..."
                      : "Search by type name or code"
                  }
                  autoComplete="off"
                  style={{
                    ...styles.input,
                    ...(errors.certificate_type_id
                      ? { borderColor: "#DC2626" }
                      : {}),
                  }}
                />

                {typeDropdownOpen && !loadingTypes && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      zIndex: 20,
                      background: "#FFFFFF",
                      border: "1px solid #DDD5EA",
                      borderRadius: 9,
                      boxShadow: "0 8px 22px rgba(40,20,70,0.12)",
                      maxHeight: 210,
                      overflowY: "auto",
                    }}
                  >
                    {filteredTypes.length ? (
                      filteredTypes.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => chooseType(item)}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            padding: "11px 13px",
                            border: 0,
                            borderBottom: "1px solid #F1ECF8",
                            background: "#FFFFFF",
                            cursor: "pointer",
                            color: "#382650",
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {item.type_name}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "#897B9B",
                              marginTop: 3,
                            }}
                          >
                            {item.type_code}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: 13,
                          color: "#897B9B",
                          fontSize: 12,
                        }}
                      >
                        No certificate types found.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {errors.certificate_type_id && (
                <span style={styles.error}>
                  {errors.certificate_type_id}
                </span>
              )}
            </div>

            <Field
              label="Certificate Name"
              name="certificate_name"
              value={form.certificate_name}
              onChange={handleChange}
              required
              placeholder="Enter certificate name"
              error={errors.certificate_name}
            />

            <Field
              label="Certificate Number"
              name="certificate_number"
              value={form.certificate_number}
              onChange={handleChange}
              placeholder="Enter certificate number"
            />

            <Field
              label="Issuing Authority"
              name="issuing_authority"
              value={form.issuing_authority}
              onChange={handleChange}
              placeholder="Enter issuing authority"
            />

            <Field
              label="Issued To"
              name="issued_to"
              value={form.issued_to}
              onChange={handleChange}
              placeholder="Enter person or organization"
            />

            <Field
              label="Branch / Location"
              name="branch"
              value={form.branch}
              onChange={handleChange}
              placeholder="Enter branch or location"
            />

            <Field
              label="Asset Reference ID"
              name="asset_reference_id"
              value={form.asset_reference_id}
              onChange={handleChange}
              placeholder="Optional asset reference"
            />
          </div>
        </section>

        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>02. Validity Details</h2>

          <div style={styles.grid}>
            <Field
              label="Issue Date"
              name="issue_date"
              type="date"
              value={form.issue_date}
              onChange={handleChange}
            />

            <Field
              label="Expiry Date"
              name="expiry_date"
              type="date"
              value={form.expiry_date}
              onChange={handleChange}
              error={errors.expiry_date}
            />
          </div>
        </section>

        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>03. Certificate Document</h2>

          <div style={styles.upload}>
            <div style={{ fontSize: 35, marginBottom: 10 }}>☁</div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#4C1D95",
                marginBottom: 7,
              }}
            >
              Upload Certificate
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#7C6C91",
                marginBottom: 13,
              }}
            >
              Choose one PDF, JPG, PNG, or ZIP file. Maximum size: 512 KB.
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.zip"
              onChange={handleFileChange}
              style={{ display: "none" }}
              aria-label="Choose certificate file"
            />

            <button
              type="button"
              style={styles.primaryButton}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </button>

            {selectedFile && (
              <div
                style={{
                  margin: "18px auto 0",
                  maxWidth: 480,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  textAlign: "left",
                  padding: 13,
                  border: "1px solid #E4D7F8",
                  borderRadius: 9,
                  background: "#FFFFFF",
                }}
              >
                <span style={{ fontSize: 25 }}>📄</span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    title={selectedFile.name}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {selectedFile.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#7C6C91",
                      marginTop: 4,
                    }}
                  >
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </div>
                </div>

                <button
                  type="button"
                  onClick={removeFile}
                  style={{
                    border: 0,
                    background: "transparent",
                    color: "#B42318",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Remove
                </button>
              </div>
            )}

            {errors.file && (
              <div style={{ ...styles.error, marginTop: 12 }}>
                {errors.file}
              </div>
            )}
          </div>
        </section>

        <section style={styles.card}>
          <h2 style={styles.sectionTitle}>04. Additional Details</h2>

          <div style={styles.field}>
            <label htmlFor="remarks" style={styles.label}>
              Remarks
            </label>
            <textarea
              id="remarks"
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={4}
              placeholder="Enter any additional information"
              style={{
                ...styles.input,
                resize: "vertical",
                minHeight: 95,
              }}
            />
          </div>
        </section>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            flexWrap: "wrap",
            paddingBottom: 24,
          }}
        >
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/assets/certificate-management")}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            type="submit"
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.7 : 1,
            }}
            disabled={saving || loadingTypes}
          >
            {saving ? "Saving..." : "Save Certificate"}
          </button>
        </div>
      </form>
    </div>
  );
}
